import { useState, useCallback, useRef } from 'react';
import { api } from '../services/api';

/**
 * useBlockEditor — Hook de edição inline de blocos
 * 
 * Responsabilidades:
 * - Gerencia estado local otimista (mostra antes de confirmar)
 * - Chama PUT /api/quotes/{id}/blocks/{blockId}
 * - Controla feedback visual (saving, saved, error)
 * - Suporta paths aninhados em details (ex: 'baggage.carry_on')
 * 
 * @param {string} quoteId - ID da cotação
 * @param {object} block - Bloco atual
 * @param {function} onBlockUpdate - Callback para atualizar estado no pai
 */
export function useBlockEditor(quoteId, block, onBlockUpdate) {
  const [saving, setSaving] = useState(false);
  const [savedField, setSavedField] = useState(null);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  /**
   * Atualiza um campo top-level do bloco (price, date, title, description)
   */
  const updateField = useCallback(async (fieldName, value) => {
    if (!quoteId || !block?.id) return;

    // Valor não mudou
    if (block[fieldName] === value) return;

    setSaving(true);
    setError(null);

    try {
      const payload = { [fieldName]: value };
      const result = await api.editBlock(quoteId, block.id, payload);

      if (result.error) {
        setError(result.error);
        return;
      }

      // Atualiza estado no pai
      if (onBlockUpdate) {
        onBlockUpdate(block.id, { ...block, [fieldName]: value });
      }

      // Feedback visual
      setSavedField(fieldName);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setSavedField(null), 1500);
    } catch (err) {
      setError(err.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  }, [quoteId, block, onBlockUpdate]);

  /**
   * Atualiza um campo dentro de details (JSONB)
   * Suporta paths aninhados: 'baggage.carry_on' → details.baggage.carry_on
   */
  const updateDetail = useCallback(async (path, value) => {
    if (!quoteId || !block?.id) return;

    const details = { ...(block.details || {}) };
    const currentValue = getNestedValue(details, path);

    // Valor não mudou
    if (currentValue === value) return;

    setSaving(true);
    setError(null);

    try {
      const updatedDetails = setNestedValue({ ...details }, path, value);
      const payload = { details: updatedDetails };
      const result = await api.editBlock(quoteId, block.id, payload);

      if (result.error) {
        setError(result.error);
        return;
      }

      // Atualiza estado no pai
      if (onBlockUpdate) {
        onBlockUpdate(block.id, { ...block, details: updatedDetails });
      }

      // Feedback visual
      setSavedField(path);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setSavedField(null), 1500);
    } catch (err) {
      setError(err.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  }, [quoteId, block, onBlockUpdate]);

  /**
   * Atualiza múltiplos campos de details de uma vez
   * Útil para bagagem (3 campos), datas (recalcular nights), etc.
   */
  const updateDetails = useCallback(async (partialDetails, feedbackKey = null) => {
    if (!quoteId || !block?.id) return;

    setSaving(true);
    setError(null);

    try {
      const updatedDetails = { ...(block.details || {}), ...partialDetails };
      const payload = { details: updatedDetails };
      const result = await api.editBlock(quoteId, block.id, payload);

      if (result.error) {
        setError(result.error);
        return;
      }

      if (onBlockUpdate) {
        onBlockUpdate(block.id, { ...block, details: updatedDetails });
      }

      setSavedField(feedbackKey || 'details');
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setSavedField(null), 1500);
    } catch (err) {
      setError(err.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  }, [quoteId, block, onBlockUpdate]);

  /**
   * Atualiza campo dentro de um segmento de voo
   * segments[index].field = value
   */
  const updateSegment = useCallback(async (segIndex, field, value) => {
    if (!quoteId || !block?.id) return;

    const details = { ...(block.details || {}) };
    const segments = [...(details.segments || [])];

    if (!segments[segIndex]) return;

    segments[segIndex] = { ...segments[segIndex], [field]: value };
    details.segments = segments;

    setSaving(true);
    setError(null);

    try {
      const payload = { details };
      const result = await api.editBlock(quoteId, block.id, payload);

      if (result.error) {
        setError(result.error);
        return;
      }

      if (onBlockUpdate) {
        onBlockUpdate(block.id, { ...block, details });
      }

      setSavedField(`segment_${segIndex}_${field}`);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setSavedField(null), 1500);
    } catch (err) {
      setError(err.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  }, [quoteId, block, onBlockUpdate]);

  return {
    saving,
    savedField,
    error,
    updateField,
    updateDetail,
    updateDetails,
    updateSegment,
    isSaved: (field) => savedField === field,
  };
}

// ── Helpers para paths aninhados ──

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

function setNestedValue(obj, path, value) {
  const keys = path.split('.');
  const last = keys.pop();
  let current = obj;

  for (const key of keys) {
    if (current[key] === undefined || current[key] === null) {
      current[key] = {};
    } else {
      current[key] = { ...current[key] };
    }
    current = current[key];
  }

  current[last] = value;
  return obj;
}
